'use client';

import React from 'react';

import { Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';

import styles from './styles.module.css';

import { SitemapSecondaryCategory } from '@/app/[locale]/site-map/categories/components/SitemapSecondaryCategory';
import { ExtendedLink } from '@/components/ExtendedLink';
import { type GroupAttribute } from '@/modules/categories/aliases';
import {
  capitalizeDepartment,
  filterProductTypesForDepartment,
  getGroupNameWithDepartment,
  getProductTypeNameWithDepartment,
  removeHyphensExceptTShirts,
} from '@/modules/sitemap/helpers';

type Props = {
  department: string;
  groups: GroupAttribute[];
};

function SitemapTopCategory({ department, groups }: Props) {
  function getDepartmentSlug(department: string): string {
    return department.replace(/wear$/i, '');
  }

  if (groups.length === 0) {
    return null;
  }

  const departmentSlug = getDepartmentSlug(department);

  return (
    <li
      key={department}
      className={clsx(styles.baseListItem, styles.topListItem)}
    >
      <Heading variant="md">
        <ExtendedLink
          prefetch={false}
          href={`/category/${departmentSlug}`}
          className={clsx(styles.baseLink, styles.firstLevelLink)}
        >
          {removeHyphensExceptTShirts(capitalizeDepartment(department))}
        </ExtendedLink>
      </Heading>

      <ul className={styles.secondLevelList}>
        {groups.map((group) => {
          const groupName = getGroupNameWithDepartment(group, department);
          const groupSlug = group.id;

          const relevantProductTypes = filterProductTypesForDepartment(
            group.product_types,
            department
          );

          return (
            <SitemapSecondaryCategory
              className={clsx(styles.baseListItem, styles.topListItem)}
              linkClassName={clsx(styles.baseLink, styles.secondLevelLink)}
              key={group.id}
              href={`/category/${departmentSlug}/${groupSlug}`}
              title={removeHyphensExceptTShirts(groupName)}
            >
              {relevantProductTypes.length > 0 ? (
                <ul className={styles.thirdLevelList}>
                  {relevantProductTypes.map((productType) => {
                    const productTypeName = getProductTypeNameWithDepartment(
                      productType,
                      department
                    );
                    const productTypeSlug = productType.id;

                    return (
                      <SitemapSecondaryCategory
                        className={clsx(
                          styles.baseListItem,
                          styles.thirdLevelItem
                        )}
                        linkClassName={styles.baseLink}
                        key={productType.id}
                        href={`/category/${departmentSlug}/${groupSlug}/${productTypeSlug}`}
                        title={removeHyphensExceptTShirts(productTypeName)}
                      />
                    );
                  })}
                </ul>
              ) : null}
            </SitemapSecondaryCategory>
          );
        })}
      </ul>
    </li>
  );
}

export { SitemapTopCategory };
